<i18n>
{
    "en_US": {
        "cancel": "Cancel",
        "save": "Save",
        "my-account": "My account",
        "configuration": "Configuration",
        "title": "Modify your account",
        "higher-less": "Higher value = find less similarities",
        "change-password": "Change your password",
        "default-note-language": "Default note language",
        "threshold-similarity": "Similarity Threshold",

        "danish": "Danish",
        "dutch": "Dutch",
        "english": "English",
        "finnish": "Finnish",
        "french": "French",
        "german": "German",
        "hungarian": "Hungarian",
        "italian": "Italian",
        "norwegian": "Norwegian",
        "portuguese": "Portuguese",
        "romanian": "Romanian",
        "russian": "Russian",
        "spanish": "Spanish",
        "turkish": "Turkish",
        "swedish": "Swedish"
    },
    "fr_FR": {
        "cancel": "Annuler",
        "save": "Sauvegarder",
        "my-account": "Mon compte",
        "configuration": "Configuration",
        "title": "Modifier mon compte",
        "higher-less": "Valeur élevée = moins de similitudes",
        "change-password": "Changement de mot de passe",
        "default-note-language": "Langue par défaut des notes",
        "threshold-similarity": "Seuil de similarité",

        "danish": "Danois",
        "dutch": "Néerlandais",
        "english": "Anglaish",
        "finnish": "Finlandais",
        "french": "Français",
        "german": "Allemand",
        "hungarian": "Hongrois",
        "italian": "Italien",
        "norwegian": "Norvégien",
        "portuguese": "Portugais",
        "romanian": "Roumain",
        "russian": "Russe",
        "spanish": "Espagnol",
        "turkish": "Turque",
        "swedish": "Suédois"
    }
}
</i18n>

<template>
    <div class="user-container" v-bind:class="{ active: isModalUserVisible }">
        <h4 v-text="$t('title')"></h4>

        <div class="left-shift">
            <h5 v-text="$t('my-account')"></h5>
            <div class="left-shift">
                <input-check-email-availability v-model="email" v-bind:escaped="this.authentication.email" v-bind:disabled="pending.update_user_account" />
            </div>
        </div>

        <div class="left-shift m40">
            <h5 v-text="$t('configuration')"></h5>
            <div class="left-shift">
                <div class="row">
                    <div class="col-6">
                        <a class="like-label" v-text="$t('default-note-language')"></a>
                        <div>
                            <select class="form-control" v-model="selected" v-bind:disabled="pending.update_user_account">
                                <option v-bind:key="language" v-for="language in supported" v-bind:value="language" v-text="$t(language)"></option>
                            </select>
                        </div>
                    </div>
                    <div class="col-6">
                        <a class="like-label" v-text="$t('threshold-similarity')"></a>
                        <div class="flex m10">
                            <p class="similarity" v-text="getFormattedSimilarity"></p>
                            <input-slider min="0.01" max="1" step="0.01" v-model="similarity" v-bind:disabled="pending.update_user_account" />
                        </div>
                        <p class="text-right">
                            <small v-text="$t('higher-less')"></small>
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div class="left-shift m40">
            <h5 v-text="$t('change-password')"></h5>
            <div class="left-shift">
                <input-password-reveal v-model="password" v-bind:disabled="pending.update_user_account" />
                <input-password-new-reveal v-model="renew" v-bind:disabled="pending.update_user_account" />
                <input-password-confirm v-model="confirm" v-bind:password="renew" v-bind:disabled="pending.update_user_account" />
            </div>
        </div>

        <p class="text-center m30">
            <a class="btn btn-default" v-on:click="hideModalUser" v-text="$t('cancel')"></a>
            <button class="btn btn-primary save" v-on:click="save" v-bind:disabled="pending.update_user_account || !isSubmitAllowed">
                <loader-basic-spinner v-if="pending.update_user_account" />
                <a v-else v-text="$t('save')"></a>
            </button>
        </p>
    </div>
</template>

<script>
    import { mapState, mapMutations, mapActions } from "vuex";
    import InputSlider from "inputs/Slider";
    import InputCheckEmailAvailability from "inputs/CheckEmailAvailability";
    import InputPasswordNewReveal from "inputs/PasswordNewReveal";
    import InputPasswordReveal from "inputs/PasswordReveal";
    import InputPasswordConfirm from "inputs/PasswordConfirm";
    import LoaderBasicSpinner from "loaders/BasicSpinner";
    import event from "plugins/event";

    export default {
        name: "User",
        computed: {
            isSubmitAllowed: function() {
                // Checking password can be submitted
                let valid = true;
                if (this.password && valid === true) {
                    valid = this.renew && this.renew === this.confirm;
                }
                if (this.renew && valid === true) {
                    valid = this.password && this.renew === this.confirm;
                }
                if (!this.email && valid === true) {
                    valid = false;
                }
                if (this.email && valid === true) {
                    valid = this.validateEmail(this.email);
                }
                return valid;
            },
            getFormattedSimilarity: function() {
                let val = "" + this.similarity;

                if (val.length === 0) {
                    val = "0";
                }
                if (val.length === 1) {
                    val += ".";
                }

                while(val.length < 4) {
                    val += "0";
                }
                return val;
            },
            ...mapState("language", ["language", "supported"]),
            ...mapState("application", ["isModalUserVisible"]),
            ...mapState("user", ["authentication", "pending"])
        },
        data: function() {
            return {
                similarity: 0.3,
                email: "",
                selected: "",
                password: "",
                renew: "",
                confirm: ""
            }
        },
        components: {
            InputSlider,
            InputCheckEmailAvailability,
            LoaderBasicSpinner,
            InputPasswordNewReveal,
            InputPasswordReveal,
            InputPasswordConfirm
        },
        mounted: function() {
            this.supportedLanguage()
                .then(function() {
                    this.$Progress.finish();
                }.bind(this))
                .catch(function() {});
        },
        watch: {
            isModalUserVisible: function(newValue) {
                // When the system load the menu,
                // we ask for server request refresh.
                if (newValue) {
                    this.refreshUserAccount()
                        .then(function() {
                            this.$Progress.finish();
                            this.email = this.authentication.email;
                            this.similarity = parseFloat(this.authentication.app_preferences.similarity) || 0.3;
                            this.selected = this.authentication.app_preferences.index_language;
                            this.renew = "";
                            this.password = "";
                            this.confirm = "";

                            // Starting material design (TODO: we need to find how to destroy this)
                            $(this.$el).find("input").bmdText();
                        }.bind(this))
                        .catch(function() {});

                    this.similarity = parseFloat(this.authentication.app_preferences.similarity) || 0.3;
                    this.email = this.authentication.email;
                    this.renew = "";
                    this.password = "";
                    this.confirm = "";
                }
            }
        },
        methods: {
            validateEmail: function(email) {
                // eslint-disable-next-line
                var re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                return re.test(String(email).toLowerCase());
            },
            save: function() {
                var forceLogout = this.email && this.email !== this.authentication.email;
                this.updateUserAccount({
                    email: this.email || undefined,
                    app_preferences: {
                        similarity: parseFloat(this.similarity),
                        index_language: this.selected || "english"
                    },
                    password: this.password || undefined,
                    new_password: this.renew || undefined
                })
                    .then(function() {
                        this.$Progress.finish();
                        this.hideModalUser();
                        // If user has changed his email, we logout...
                        if (forceLogout) {
                            event.$emit("force-logout");
                        }
                    }.bind(this))
                    .catch(function() {
                        this.hideModalUser();
                    }.bind(this));
            },
            ...mapMutations("application", ["hideModalUser"]),
            ...mapActions("language", ["supportedLanguage"]),
            ...mapActions("user", ["refreshUserAccount", "updateUserAccount"])
        }
    }
</script>

<style scoped>
    .user-container {
        display: none;
        position: fixed;
        left: 50%;
        top: 50%;
        height: 720px;
        width: 600px;
        margin-left: -300px;
        margin-top: -360px;
        z-index: 11;
        background: #fff;
        color: #212529;
        padding: 17px;
        -webkit-border-radius: 2px;
        -moz-border-radius: 2px;
        border-radius: 2px;
        -webkit-transition: all .2s ease;
        -moz-transition: all .2s ease;
        -ms-transition: all .2s ease;
        transition: all .2s ease;
    }
    .save[disabled] {
        margin-bottom: 0;
        width: 60px;
        height: 36px;
    }
    .save[disabled] >>> .lds-ring {
        margin-top: -10px;
        margin-left: -10px;
    }

    .user-container .m10 {
        margin-top: 10px;
    }

    .user-container .m30 {
        margin-top: 30px;
    }

    .user-container .m40 {
        margin-top: 40px;
    }

    .left-shift {
        padding-left: 10px;
    }

    .similarity {
        margin-right: 10px;
    }

    .bmd-form-group label {
        font-weight: 500 !important;
    }

    .user-container p {
        margin-bottom: 0;
    }

    .flex {
        display: flex;
    }
    h4 {
        margin-bottom: 20px;
    }
    h5 {
        font-weight: 200;
    }

    .like-label {
        color: rgba(0,0,0,.26) !important;
        font-weight: 500 !important;
        font-size: 0.75rem;
    }

    .user-container >>> .is-focused label,
    .user-container >>> .is-filled label {
        font-weight: 500 !important;
        font-size: 0.75rem;
    }

    .user-container.active {
        animation: showSweetAlert .3s;
        display: block;
    }
    .btn-primary, .btn-default {
        text-transform: uppercase !important;
    }

    @media screen and (max-width: 600px), screen and (max-height: 500px) {
        .user-container {
            left: 0;
            top: 0;
            height: 100%;
            width: 100%;
            margin-left: 0;
            margin-top: 0;
            -webkit-border-radius: 0px;
            -moz-border-radius: 0px;
            border-radius: 0px;
        }
    }
</style>
